import { State } from '../store';

/**
 * Resolve the team id the team-mode dashboard should query against.
 *
 * Checks `activeTeamOverride` first (set by the sidebar team switcher),
 * then reads `state.user.team` (set when the user is a non-admin
 * member of a team) and falls back to `ownedTeams[0]` (set when the
 * user is an admin). Returns null when neither is populated, so
 * callers can short-circuit the team-only data fetches.
 */
export function selectActiveTeamId(state: State): string | null {
  const memberships = state.user.teamMemberships ?? [];
  const override = state.user.activeTeamOverride;
  if (override) {
    const overrideIsValid = memberships.length
      ? memberships.some((team) => team.id === override)
      : [state.user.team, ...(state.user.ownedTeams ?? [])].some(
          (team) => team?.id === override
        );
    if (overrideIsValid) return override;
  }
  if (
    state.user.activeTeamId &&
    memberships.some((team) => team.id === state.user.activeTeamId)
  )
    return state.user.activeTeamId;
  if (memberships[0]) return memberships[0].id;
  return state.user.team?.id ?? state.user.ownedTeams?.[0]?.id ?? null;
}

/**
 * The distinct teams the user belongs to (member team + owned/admin teams),
 * de-duped by id. Used to decide whether to show a team switcher — most users
 * have exactly one, in which case there is nothing to switch between.
 */
export interface UserTeamOption {
  id: string;
  name: string;
  username: string | null;
  profileImageUrl: string | null;
}

export function selectUserTeams(state: State): UserTeamOption[] {
  if (state.user.teamMemberships?.length) {
    return state.user.teamMemberships.map(
      ({ id, name, username, profileImageUrl }) => ({
        id,
        name,
        username: username ?? null,
        profileImageUrl: profileImageUrl ?? null,
      })
    );
  }
  const teams = [
    ...(state.user.team ? [state.user.team] : []),
    ...(state.user.ownedTeams ?? []),
  ];
  const byId = new Map<string, UserTeamOption>(
    teams.map((t) => [
      t.id,
      {
        id: t.id,
        name: t.name,
        username: t.username ?? null,
        profileImageUrl: t.profileImageUrl ?? null,
      },
    ])
  );
  return [...byId.values()];
}

export function selectIsTeamAdmin(state: State): boolean {
  const activeId = selectActiveTeamId(state);
  if (!activeId) return false;
  const membership = state.user.teamMemberships?.find(
    (team) => team.id === activeId
  );
  if (membership) return membership.roleType === 'admin';
  if (state.user.team?.id === activeId && state.user.team.roleType === 'admin')
    return true;
  return (state.user.ownedTeams ?? []).some((t) => t.id === activeId);
}
